import { Controller, ControllerProps, FieldValues } from 'react-hook-form';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { Input, InputProps } from '@chakra-ui/react';
import {
  convertDateIntoDateTimeString,
  DATE_FORMAT,
  parseDateTimeString,
} from 'utils/dateUtils';

export type DateTimePickerProps<TFieldValues extends FieldValues> = {
  controllerProps: Omit<ControllerProps<TFieldValues>, 'render'>;
  datePickerProps?: Omit<
    ReactDatePickerProps,
    | 'customInput'
    | 'onChange'
    | 'onBlur'
    | 'selected'
    | 'dateFormat'
    | 'showTimeSelect'
    | 'showPopperArrow'
  >;
  inputProps?: InputProps;
  /** バリューを string として扱うか */
  asString?: boolean;
};

const getSelectedDate = (value: unknown) => {
  if (typeof value === 'string')
    return value ? parseDateTimeString(value) : undefined;

  return value as Date | null | undefined;
};

const handleChange =
  (onChange: (...event: unknown[]) => void, asString: boolean) =>
  (date: Date | null) => {
    if (asString) {
      const dateStr = date ? convertDateIntoDateTimeString(date) : '';
      onChange(dateStr);
    } else {
      onChange(date);
    }
  };

export const DateTimePicker = <TFieldValues extends FieldValues>({
  controllerProps,
  datePickerProps: { timeIntervals, ...datePickerProps } = {},
  inputProps,
  asString = false,
}: DateTimePickerProps<TFieldValues>) => (
  <Controller
    {...controllerProps}
    render={({ field: { onChange, onBlur, value } }) => (
      <ReactDatePicker
        // NOTE: Pickerの表示幅が`input`要素に依存しており、かつ時刻付のPickerは`328px`必要なため、`minWidth`を設定する。
        customInput={<Input minWidth="328px" {...inputProps} />}
        onChange={handleChange(onChange, asString)}
        onBlur={onBlur}
        selected={getSelectedDate(value)}
        dateFormat={DATE_FORMAT.DATE_TIME}
        showTimeSelect
        showPopperArrow={false}
        timeIntervals={timeIntervals ?? 60}
        popperProps={{
          strategy: 'fixed',
        }}
        {...datePickerProps}
      />
    )}
  />
);
